import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import {
  AllowanceModelDto,
  CORRELATION_ID_HEADER,
  DocumentType,
  OEPPaymentMethod,
  OpenEnrollmentPeriodDto,
  OpenEnrollmentPeriodUpdateErrorDto,
} from '@zorro/clients';
import {
  ERROR_MESSAGES,
  logger,
  responseErrorToString,
} from '@zorro/common-utils';
import {
  formatDateISO,
  getNow,
  parseDate,
  parseDateISO,
} from '@zorro/shared/formatters';
import {
  callEndpoint,
  getEarliestOpenEnrollmentPeriod,
  getLatestOpenEnrollmentPeriod,
  showErrorNotification,
  showWarningNotification,
  useEmployerFeatureFlag,
  useForm,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import axios from 'axios';
import csv from 'csvtojson';
import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import {
  CoverageScope,
  PlanYearSetupFormFields,
  getPlanYearSetupSchema,
} from './PlanYearSetupUtils';

type UsePlanYearSetupForm = (props: {
  isFinalizationMode: boolean;
  shouldFallbackToEarliestPeriod?: boolean;
  selectedOpenEnrollmentPeriodId?: string;
  employerId?: string;
}) => {
  planYearSetupForm: UseFormReturn<PlanYearSetupFormFields>;
  openEnrollmentPeriodId?: string;
  isAllowanceModelRequired: boolean;
  isExistingModelRemoved: boolean;
  allowanceModelSourceNote: string | null;
  allowanceModelSourceLabel: string | null;
  existingAllowanceModel: AllowanceModelDto | null;
  planYearUpdateErrors: OpenEnrollmentPeriodUpdateErrorDto[];
  onSubmit: () => Promise<{
    success: boolean;
    oepCorrelationId?: string | null;
    allowanceModelCorrelationId?: string | null;
  }>;
};

export const usePlanYearSetupForm: UsePlanYearSetupForm = ({
  employerId,
  selectedOpenEnrollmentPeriodId,
  shouldFallbackToEarliestPeriod = false,
  isFinalizationMode,
}) => {
  const queryClient = useQueryClient();
  const idempotencyKey = useIdempotencyKey();
  const [planYearUpdateErrors, setPlanYearUpdateErrors] = useState<
    OpenEnrollmentPeriodUpdateErrorDto[]
  >([]);

  const { data: openEnrollmentPeriods = [] } = useMonolithQuery({
    method: 'openEnrollmentPeriodsControllerFindAllForEmployer',
    params: employerId && [employerId],
  });

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: employerId && [employerId],
  });

  const { isOn: isLynxPaymentsEnabled } = useEmployerFeatureFlag(
    'lynx_payments_enabled',
    employerId
  );

  const {
    data: employeeCount = 0,
    isLoading: isEmployeeCountLoading,
    isError: isEmployeeCountError,
  } = useMonolithQuery({
    method: 'employeesControllerCountByEmployer',
    params: employerId && [employerId],
  });

  const hasEmployees = employeeCount > 0;
  const isAllowanceModelRequired =
    isEmployeeCountLoading || isEmployeeCountError || hasEmployees;

  const latestOpenEnrollmentPeriod = getLatestOpenEnrollmentPeriod(
    openEnrollmentPeriods
  );
  const latestCoverageStart = latestOpenEnrollmentPeriod?.effectiveFrom;

  let openEnrollmentPeriod: OpenEnrollmentPeriodDto | undefined;
  if (selectedOpenEnrollmentPeriodId) {
    openEnrollmentPeriod = openEnrollmentPeriods.find(
      ({ id }) => id === selectedOpenEnrollmentPeriodId
    );
  } else if (shouldFallbackToEarliestPeriod) {
    openEnrollmentPeriod = getEarliestOpenEnrollmentPeriod(
      openEnrollmentPeriods
    );
  }

  const getDefaultEffectiveDates = (): [Date, Date] => {
    if (openEnrollmentPeriod) {
      const { effectiveFrom, effectiveUntil } = openEnrollmentPeriod;
      return [
        parseDateISO(effectiveFrom).toDate(),
        parseDateISO(effectiveUntil).toDate(),
      ];
    }

    if (latestCoverageStart) {
      const coverageStartDate = parseDateISO(latestCoverageStart);
      const nextYear = coverageStartDate.add(1, 'year');

      return [
        nextYear.startOf('year').toDate(),
        nextYear.endOf('year').toDate(),
      ];
    } else if (employer?.prospectCoverageStartDate) {
      const coverageStartDate = parseDateISO(
        employer.prospectCoverageStartDate
      );

      return [
        coverageStartDate.toDate(),
        coverageStartDate.endOf('year').toDate(),
      ];
    }

    const nextYear = getNow().add(1, 'year');
    return [nextYear.startOf('year').toDate(), nextYear.endOf('year').toDate()];
  };

  const planYearSetupForm = useForm<PlanYearSetupFormFields>({
    mode: 'all',
    resolver: yupResolver(
      getPlanYearSetupSchema(
        isFinalizationMode,
        isLynxPaymentsEnabled,
        isAllowanceModelRequired,
        {
          initialDrawDate: openEnrollmentPeriod?.initialDrawDate,
        }
      )
    ),
    defaultValues: {
      effectiveDates: getDefaultEffectiveDates(),
      oepDates: openEnrollmentPeriod
        ? [
            parseDateISO(openEnrollmentPeriod.onboardingFrom).toDate(),
            parseDateISO(openEnrollmentPeriod.onboardingUntil).toDate(),
          ]
        : undefined,
      paymentMethod:
        openEnrollmentPeriod?.paymentMethod || OEPPaymentMethod.ZORRO_PAY,
      initialDrawAmount: openEnrollmentPeriod?.initialDrawAmount ?? undefined,
      initialDrawDate: openEnrollmentPeriod?.initialDrawDate
        ? parseDateISO(openEnrollmentPeriod?.initialDrawDate).toDate()
        : undefined,
      reserveAmount: openEnrollmentPeriod?.reserveAmount ?? undefined,
      thresholdAmount: openEnrollmentPeriod?.thresholdAmount ?? undefined,
      allowanceModelClassDefinitions: null,
      isAORChanged: openEnrollmentPeriod?.isAORChanged || false,
      coverageScope:
        openEnrollmentPeriod?.isDentalEnabled ||
        openEnrollmentPeriod?.isVisionEnabled ||
        openEnrollmentPeriod?.isQmeEnabled
          ? CoverageScope.EXPANDED
          : CoverageScope.MEDICAL_ONLY,
      dentalCoverage: openEnrollmentPeriod?.isDentalEnabled ?? false,
      visionCoverage: openEnrollmentPeriod?.isVisionEnabled ?? false,
      qmeCoverage: openEnrollmentPeriod?.isQmeEnabled ?? false,
      isDomesticPartnerAllowed:
        openEnrollmentPeriod?.isDomesticPartnerAllowed ??
        latestOpenEnrollmentPeriod?.isDomesticPartnerAllowed ??
        true,
    },
  });

  const { watch, setValue } = planYearSetupForm;

  const effectiveDatesRange = watch('effectiveDates');
  const [effectiveFrom] = effectiveDatesRange || [];
  const planYear = effectiveFrom?.getFullYear();

  const { data: allowanceModel } = useMonolithQuery({
    method: 'allowanceModelControllerSearch',
    params: employerId && planYear && [employerId, { year: planYear }],
  });

  const { data: adminEmployerDocuments } = useMonolithQuery({
    method: 'employerDocumentControllerGetDocumentsByType',
    params: employerId && [employerId, DocumentType.ADMIN],
  });

  const { data: allowanceModelSource } = useMonolithQuery({
    method: 'allowanceModelSourceControllerFindSourceMetadata',
    params: employerId && planYear && [employerId, planYear],
  });

  const { tryMutate: tryDeleteAllowanceModel } = useMonolithMutation({
    method: 'allowanceModelControllerDelete',
    shouldShowLoadingOverlay: false,
    shouldReloadPage: false,
  });

  useEffect(() => {
    if (allowanceModel) {
      setValue('allowanceModel', allowanceModel, { shouldValidate: true });
    }
  }, [allowanceModel, setValue]);

  useEffect(() => {
    if (allowanceModelSource) {
      setValue('allowanceModelSource', {
        quoteId: allowanceModelSource.quoteId,
        planDesignId: allowanceModelSource.planDesignId,
        adjustmentNote: allowanceModelSource.adjustmentNote,
      });
    }
  }, [allowanceModelSource, setValue]);

  const allowanceModelSourceNote =
    allowanceModelSource && allowanceModelSource.manualUpload
      ? allowanceModelSource.adjustmentNote
      : null;

  const getAllowanceModelSourceLabel = () => {
    if (!allowanceModelSource) {
      return null;
    }
    return allowanceModelSource.manualUpload
      ? 'Uploaded manually'
      : 'Applied from quote';
  };

  const isExistingModelRemoved =
    Boolean(allowanceModel) && !watch('allowanceModel');

  // eslint-disable-next-line sonarjs/cognitive-complexity
  async function onSubmit(): Promise<{
    success: boolean;
    oepCorrelationId?: string | null;
    allowanceModelCorrelationId?: string | null;
  }> {
    const data = planYearSetupForm.getValues();

    const [planYearStartDate, planYearEndDate] = data.effectiveDates || [];
    const [onboardingPeriodStartDate, onboardingPeriodEndDate] =
      data.oepDates || [];

    if (!employerId) {
      showErrorNotification({
        message: 'Employer id is missing',
      });
      return { success: false };
    }

    if (
      !planYearStartDate ||
      !planYearEndDate ||
      !onboardingPeriodStartDate ||
      !onboardingPeriodEndDate
    ) {
      showErrorNotification({
        message:
          'Election window and coverage period are required to set up a plan year',
      });
      return { success: false };
    }

    const isSelfPay = data.paymentMethod === OEPPaymentMethod.SELF_PAY;

    const body = {
      effectiveFrom: formatDateISO(planYearStartDate),
      effectiveUntil: formatDateISO(planYearEndDate),
      onboardingFrom: formatDateISO(onboardingPeriodStartDate),
      onboardingUntil: formatDateISO(onboardingPeriodEndDate),
      paymentMethod: data.paymentMethod,
      initialDrawAmount:
        !isSelfPay && data.initialDrawAmount ? data.initialDrawAmount : null,
      initialDrawDate:
        !isSelfPay && data.initialDrawDate
          ? formatDateISO(data.initialDrawDate)
          : null,
      reserveAmount:
        !isSelfPay && data.reserveAmount ? data.reserveAmount : null,
      thresholdAmount:
        !isSelfPay && data.thresholdAmount ? data.thresholdAmount : null,
      isAORChanged: data.isAORChanged ?? undefined,
      isDentalEnabled: data.dentalCoverage,
      isVisionEnabled: data.visionCoverage,
      isQmeEnabled: data.qmeCoverage,
      isDomesticPartnerAllowed: data.isDomesticPartnerAllowed,
    };

    let allowanceModelCorrelationId: string | null = null;
    if (data.allowanceModel && data.allowanceModel instanceof File) {
      allowanceModelCorrelationId = uuidv4();
      try {
        const contents = await data.allowanceModel.text();
        const parsedFile = await csv({ eol: '\n' }).fromString(contents);
        if (parsedFile.length === 0) {
          showErrorNotification({
            message: ERROR_MESSAGES.NO_DATA_FOUND_IN_THE_FILE_ERROR_MESSAGE,
          });
        }

        const allowanceModelPayload = {
          year: parseDate(planYearStartDate).year(),
          useExcessStipend: false,
          items: parsedFile,
          classDefinitionItems:
            data?.allowanceModelClassDefinitions?.classDefinitions,
        };

        const { data: uploadAllowanceModelResponse } = await axios.put(
          `/api/employers/${employerId}/upload-allowance-model`,
          allowanceModelPayload,
          {
            headers: {
              [CORRELATION_ID_HEADER]: allowanceModelCorrelationId,
            },
          }
        );

        if (uploadAllowanceModelResponse.warning) {
          showWarningNotification({
            message: uploadAllowanceModelResponse.warning,
          });
        }

        await saveAllowanceModelSource(
          employerId,
          allowanceModelPayload.year,
          data.allowanceModelSource
        );
      } catch (error) {
        logger.error(error);
        showErrorNotification({ message: responseErrorToString(error) });
        return { success: false };
      }
    } else if (!data.allowanceModel && allowanceModel) {
      const result = await tryDeleteAllowanceModel([
        employerId,
        allowanceModel.year,
      ]);
      if (result.isErr()) {
        return { success: false };
      }
      queryClient.removeQueries({
        queryKey: ['allowanceModelControllerSearch', employerId],
      });
    }

    const oepCorrelationId = uuidv4();
    let createdOepId = undefined;
    try {
      if (openEnrollmentPeriod) {
        const updateErrors = await callEndpoint({
          method: 'openEnrollmentPeriodsControllerUpdate',
          params: [openEnrollmentPeriod.id, idempotencyKey.get(), body],
          headers: { [CORRELATION_ID_HEADER]: oepCorrelationId },
        });

        setPlanYearUpdateErrors(updateErrors);

        if (updateErrors.length > 0) {
          return { success: false };
        }
      } else {
        const createdOep = await callEndpoint({
          method: 'openEnrollmentPeriodsControllerCreate',
          params: [idempotencyKey.get(), { ...body, employerId }],
          headers: { [CORRELATION_ID_HEADER]: oepCorrelationId },
        });
        createdOepId = createdOep.id;
      }
    } catch (error) {
      logger.error(error);
      showErrorNotification({ message: responseErrorToString(error) });
      return { success: false };
    }

    if (adminEmployerDocuments) {
      const legalAgreement = adminEmployerDocuments.find(
        (doc) => doc.isLegalAgreement
      );
      if (openEnrollmentPeriods.length === 0 && legalAgreement) {
        await callEndpoint({
          method: 'employerDocumentControllerUpdateDocument',
          params: [legalAgreement.id, { openEnrollmentPeriodId: createdOepId }],
        });
      }
    }

    setTimeout(async () => {
      await queryClient.invalidateQueries();
    }, 0);
    return {
      success: true,
      oepCorrelationId,
      allowanceModelCorrelationId,
    };
  }

  return {
    openEnrollmentPeriodId: openEnrollmentPeriod?.id,
    planYearSetupForm,
    isAllowanceModelRequired,
    isExistingModelRemoved,
    allowanceModelSourceNote,
    allowanceModelSourceLabel: getAllowanceModelSourceLabel(),
    existingAllowanceModel: allowanceModel ?? null,
    planYearUpdateErrors,
    onSubmit,
  };
};

async function saveAllowanceModelSource(
  employerId: string,
  year: number,
  source: PlanYearSetupFormFields['allowanceModelSource']
): Promise<void> {
  const { quoteId, planDesignId, adjustmentNote } = source ?? {};

  if (quoteId && planDesignId) {
    await callEndpoint({
      method: 'allowanceModelSourceControllerUpsertManualSource',
      params: [
        employerId,
        year,
        { quoteId, planDesignId, adjustmentNote: adjustmentNote?.trim() },
      ],
    });
    return;
  }

  await callEndpoint({
    method: 'allowanceModelSourceControllerDeleteSource',
    params: [employerId, year],
  });
}
